import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { readNarrativeTemplates } from '@api/narratives';
import { getFormattedLongDate, MeatballsIcon, TableList } from '@utilities';

import styles from './_index.module.scss';

const TemplateNameCell = ({ row }) => {
  const description = row?.original?.description;

  return (
    <div className={styles['custom-templates-name']}>
      <div className={styles['custom-templates-title']}>{row?.original?.name}</div>
      <p>{description || 'No description'}</p>
    </div>
  );
};

TemplateNameCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      description: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
};

const MetaValueCell = ({ value }) => (
  <div className={styles['custom-templates-meta-value']}>{value}</div>
);

MetaValueCell.propTypes = {
  value: PropTypes.node,
};

const LastUpdatedCell = ({ value }) => (
  <div className={styles['custom-templates-meta-value']}>
    {getFormattedLongDate(new Date(value))}
  </div>
);

LastUpdatedCell.propTypes = {
  value: PropTypes.string,
};

const CustomTemplates = () => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const initialSortBy = useMemo(() => [{ desc: true, id: 'lastUpdated' }], []);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setError('');
        setIsLoading(true);
        const response = await readNarrativeTemplates();
        setTemplates(response);
      } catch (err) {
        console.error(err);
        setError('Unable to load templates. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const renderEmptyMessage = () => {
    if (error) {
      return <p>{error}</p>;
    }

    return (
      <>
        <h3>No custom templates to show</h3>
        <p>Create a custom template from a Narrative to reuse its structure in future work.</p>
      </>
    );
  };

  const columns = useMemo(
    () => [
      {
        accessor: 'name',
        Cell: TemplateNameCell,
        Header: 'Template Name',
        width: 900,
      },
      {
        accessor: 'creator',
        Cell: MetaValueCell,
        Header: 'Owner',
        width: 120,
      },
      {
        accessor: 'lastUpdated',
        Cell: LastUpdatedCell,
        Header: 'Last Updated',
        width: 120,
      },
      {
        Cell: () => (
          <button
            aria-label="Template actions"
            className={styles['custom-templates-actions']}
            type="button"
          >
            <MeatballsIcon />
          </button>
        ),
        disableSortBy: true,
        Header: '',
        id: 'actions',
        width: 40,
      },
    ],
    []
  );

  return (
    <div className={styles['custom-templates']}>
      <TableList
        columns={columns}
        currentPage={1}
        data={templates}
        enableNameCellClick={false}
        enableRowActions={false}
        enableRowExpand={false}
        hasSelectionColumn={false}
        hideTrailingCellsOnHover={false}
        initialSortBy={initialSortBy}
        isLoading={isLoading}
        pageCount={0}
        renderEmptyMessage={renderEmptyMessage}
        rowClassName={styles['custom-templates-row']}
        selectedRows={[]}
        showFirstColumnDivider={false}
        showPagination={false}
      />
    </div>
  );
};

export default CustomTemplates;
